import type { Locale } from "../i18n/translations.js";

export const contactCategories = [
  "IMMEDIATE_EMERGENCY",
  "MEDICAL_HEALTH",
  "DISASTER_WEATHER",
  "FIRE_RESCUE",
  "POLICE_PUBLIC_SAFETY",
  "POISON_CHEMICAL",
  "UTILITIES",
  "ROAD_TRAVEL",
  "SOCIAL_SUPPORT",
  "ANIMAL_PET",
  "LOCAL_REGIONAL",
] as const;

export type ContactCategory = (typeof contactCategories)[number];
export type ContactReviewStatus = "DRAFT" | "REVIEW_REQUIRED" | "UNAVAILABLE";
export type ContactEvidenceStatus =
  "SOURCE_BOUND_DRAFT" | "SCOPE_UNDER_REVIEW" | "LOCAL_REGIONAL_SERVICE";

export interface ContactDirectoryRecord {
  readonly id: string;
  readonly serviceName: string;
  readonly phoneNumber: string;
  readonly dialNumber: string;
  readonly category: ContactCategory;
  readonly purposeLabel: string;
  readonly jurisdiction: string;
  readonly language: Locale;
  readonly sourceAuthority: string;
  readonly sourceUrl: string;
  readonly sourceRetrievedAt: string;
  readonly lastReviewedAt: string;
  readonly reviewStatus: ContactReviewStatus;
  readonly evidenceStatus: ContactEvidenceStatus;
  readonly openingHoursNote?: string;
  readonly aliases: readonly string[];
  readonly priority: number;
  readonly recordKind: "OFFICIAL_SOURCE_BACKED" | "FICTIONAL_FIXTURE";
  readonly editorialRecordId: string;
}

interface LocalizedContactText {
  readonly serviceName: string;
  readonly purposeLabel: string;
  readonly jurisdiction: string;
  readonly sourceAuthority: string;
  readonly openingHoursNote?: string;
  readonly aliases: readonly string[];
}
interface ContactSourceEntry {
  readonly key: string;
  readonly phoneNumber: string;
  readonly dialNumber: string;
  readonly category: ContactCategory;
  readonly sourceUrl: string;
  readonly evidenceStatus: ContactEvidenceStatus;
  readonly priority: number;
  readonly th: LocalizedContactText;
  readonly en: LocalizedContactText;
}

const sourceRetrievedAt = "2026-09-30";
const lastReviewedAt = "2026-09-30";
export const contactDirectoryDataDate = sourceRetrievedAt;

const sourceEntries: readonly ContactSourceEntry[] = [
  {
    key: "niem-1669",
    phoneNumber: "1669",
    dialNumber: "1669",
    category: "IMMEDIATE_EMERGENCY",
    sourceUrl: "https://www.niems.go.th/1/Ebook/Detail/8476?group=22",
    evidenceStatus: "SOURCE_BOUND_DRAFT",
    priority: 1,
    th: {
      serviceName: "สายด่วนการแพทย์ฉุกเฉิน",
      purposeLabel: "แจ้งเหตุเจ็บป่วยฉุกเฉิน",
      jurisdiction: "ประเทศไทย",
      sourceAuthority: "สถาบันการแพทย์ฉุกเฉินแห่งชาติ",
      aliases: ["ฉุกเฉิน", "แพทย์", "รถพยาบาล", "เจ็บป่วยฉุกเฉิน"],
    },
    en: {
      serviceName: "Emergency Medical Hotline",
      purposeLabel: "Report an emergency illness or request emergency medical assistance",
      jurisdiction: "Thailand",
      sourceAuthority: "National Institute for Emergency Medicine",
      aliases: ["emergency", "medical", "ambulance", "emergency illness"],
    },
  },
  {
    key: "dmh-1323",
    phoneNumber: "1323",
    dialNumber: "1323",
    category: "MEDICAL_HEALTH",
    sourceUrl: "https://dmh.go.th/",
    evidenceStatus: "SOURCE_BOUND_DRAFT",
    priority: 2,
    th: {
      serviceName: "สายด่วนสุขภาพจิต",
      purposeLabel: "ปรึกษาปัญหาสุขภาพจิต ไม่ใช่บริการจัดส่งการแพทย์ฉุกเฉิน",
      jurisdiction: "ประเทศไทย",
      sourceAuthority: "กรมสุขภาพจิต",
      openingHoursNote: "24 ชั่วโมง",
      aliases: ["สุขภาพจิต", "เครียด", "กังวล", "ปรึกษา"],
    },
    en: {
      serviceName: "Mental Health Hotline",
      purposeLabel: "Mental-health consultation; not emergency medical dispatch",
      jurisdiction: "Thailand",
      sourceAuthority: "Department of Mental Health",
      openingHoursNote: "24 hours",
      aliases: ["mental health", "stress", "anxiety", "consultation"],
    },
  },
  {
    key: "tmd-1182",
    phoneNumber: "1182",
    dialNumber: "1182",
    category: "DISASTER_WEATHER",
    sourceUrl: "https://www.tmd.go.th/info/info.php",
    evidenceStatus: "SOURCE_BOUND_DRAFT",
    priority: 3,
    th: {
      serviceName: "สายด่วนกรมอุตุนิยมวิทยา",
      purposeLabel: "สอบถามและติดตามข้อมูลสภาพอากาศ ไม่ใช่บริการกู้ภัย",
      jurisdiction: "ข้อมูลสภาพอากาศประเทศไทย",
      sourceAuthority: "กรมอุตุนิยมวิทยา",
      aliases: ["อากาศ", "พายุ", "ฝน", "พยากรณ์"],
    },
    en: {
      serviceName: "Thai Meteorological Department Hotline",
      purposeLabel: "Weather and official forecast information; not rescue dispatch",
      jurisdiction: "Weather information for Thailand",
      sourceAuthority: "Thai Meteorological Department",
      aliases: ["weather", "storm", "rain", "forecast"],
    },
  },
  {
    key: "tourist-police-1155",
    phoneNumber: "1155",
    dialNumber: "1155",
    category: "POLICE_PUBLIC_SAFETY",
    sourceUrl: "https://www.touristpolice.go.th/main",
    evidenceStatus: "SOURCE_BOUND_DRAFT",
    priority: 4,
    th: {
      serviceName: "สายด่วนตำรวจท่องเที่ยว",
      purposeLabel: "ขอข้อมูลและความช่วยเหลือจากตำรวจท่องเที่ยว",
      jurisdiction: "ประเทศไทย — เฉพาะภารกิจตำรวจท่องเที่ยว",
      sourceAuthority: "กองบัญชาการตำรวจท่องเที่ยว",
      aliases: ["นักท่องเที่ยว", "ตำรวจท่องเที่ยว", "ท่องเที่ยว"],
    },
    en: {
      serviceName: "Tourist Police Hotline",
      purposeLabel: "Information and assistance from the Tourist Police",
      jurisdiction: "Thailand — Tourist Police functions only",
      sourceAuthority: "Tourist Police Bureau",
      aliases: ["tourist", "tourist police", "travel assistance"],
    },
  },
  {
    key: "pea-1129",
    phoneNumber: "1129",
    dialNumber: "1129",
    category: "UTILITIES",
    sourceUrl: "https://www.pea.co.th/contact-us",
    evidenceStatus: "SOURCE_BOUND_DRAFT",
    priority: 5,
    th: {
      serviceName: "ศูนย์บริการข้อมูลผู้ใช้ไฟฟ้า PEA",
      purposeLabel: "สอบถามหรือแจ้งปัญหาระบบไฟฟ้าในพื้นที่ กฟภ.",
      jurisdiction: "74 จังหวัด ยกเว้นกรุงเทพมหานคร นนทบุรี และสมุทรปราการ",
      sourceAuthority: "การไฟฟ้าส่วนภูมิภาค",
      openingHoursNote: "24 ชั่วโมง",
      aliases: ["ไฟดับ", "ไฟฟ้า", "กฟภ", "PEA"],
    },
    en: {
      serviceName: "PEA Contact Center",
      purposeLabel: "Electricity-service information and problem reporting in the PEA service area",
      jurisdiction: "74 provinces, excluding Bangkok, Nonthaburi and Samut Prakan",
      sourceAuthority: "Provincial Electricity Authority",
      openingHoursNote: "24 hours",
      aliases: ["power outage", "electricity", "PEA"],
    },
  },
  {
    key: "doh-1586",
    phoneNumber: "1586",
    dialNumber: "1586",
    category: "ROAD_TRAVEL",
    sourceUrl: "https://www.doh.go.th/content/1765",
    evidenceStatus: "SOURCE_BOUND_DRAFT",
    priority: 6,
    th: {
      serviceName: "สายด่วนกรมทางหลวง",
      purposeLabel: "สอบถามเส้นทาง การจราจร และแจ้งเหตุบนโครงข่ายกรมทางหลวง",
      jurisdiction: "โครงข่ายในความรับผิดชอบของกรมทางหลวง",
      sourceAuthority: "กรมทางหลวง",
      openingHoursNote: "โทรฟรี 24 ชั่วโมง",
      aliases: ["ทางหลวง", "ถนน", "จราจร", "เส้นทาง"],
    },
    en: {
      serviceName: "Department of Highways Hotline",
      purposeLabel: "Route and traffic information and incident reports on the DOH network",
      jurisdiction: "Road network under the Department of Highways",
      sourceAuthority: "Department of Highways",
      openingHoursNote: "Free call, 24 hours",
      aliases: ["highway", "road", "traffic", "route"],
    },
  },
  {
    key: "ddpm-1784",
    phoneNumber: "1784",
    dialNumber: "1784",
    category: "DISASTER_WEATHER",
    sourceUrl:
      "https://backofficeminisite.disaster.go.th/apiv1/apps/minisite_cco/204/sitedownload/32433/download?TypeMenu=MainMenu&filename=19c449d03c10294f9cf6be324369e85f.pdf",
    evidenceStatus: "SCOPE_UNDER_REVIEW",
    priority: 7,
    th: {
      serviceName: "สายด่วนนิรภัย ปภ.",
      purposeLabel: "แจ้งเหตุด่วนสาธารณภัยและประสานขอความช่วยเหลือ",
      jurisdiction: "ประเทศไทย — ขอบเขตการส่งต่อและเวลาให้บริการยังอยู่ระหว่างตรวจสอบ",
      sourceAuthority: "กรมป้องกันและบรรเทาสาธารณภัย",
      aliases: ["ปภ", "สาธารณภัย", "น้ำท่วม", "ภัยพิบัติ", "แจ้งเหตุ"],
    },
    en: {
      serviceName: "DDPM Disaster Safety Hotline",
      purposeLabel: "Report an urgent disaster incident and coordinate a request for assistance",
      jurisdiction: "Thailand — routing coverage and service hours remain under review",
      sourceAuthority: "Department of Disaster Prevention and Mitigation",
      aliases: ["DDPM", "disaster", "flood", "emergency report", "safety hotline"],
    },
  },
  {
    key: "rtp-191",
    phoneNumber: "191",
    dialNumber: "191",
    category: "POLICE_PUBLIC_SAFETY",
    sourceUrl:
      "https://www.royalthaipolice.go.th/downloads/%E0%B8%84%E0%B8%B9%E0%B9%88%E0%B8%A1%E0%B8%B7%E0%B8%AD%E0%B8%9B%E0%B8%A3%E0%B8%B0%E0%B8%8A%E0%B8%B2%E0%B8%8A%E0%B8%99.pdf",
    evidenceStatus: "SCOPE_UNDER_REVIEW",
    priority: 7,
    th: {
      serviceName: "เหตุด่วนเหตุร้าย",
      purposeLabel: "แจ้งเหตุด่วนเหตุร้าย",
      jurisdiction: "ขอบเขตการรับแจ้งทั่วประเทศยังอยู่ระหว่างตรวจสอบ",
      sourceAuthority: "สำนักงานตำรวจแห่งชาติ",
      aliases: ["ตำรวจ", "เหตุด่วน", "เหตุร้าย", "แจ้งเหตุ"],
    },
    en: {
      serviceName: "Emergency Police Call",
      purposeLabel: "Report an urgent incident or crime",
      jurisdiction: "Nationwide routing remains under review",
      sourceAuthority: "Royal Thai Police",
      aliases: ["police", "urgent incident", "crime", "emergency call"],
    },
  },
  {
    key: "bma-199",
    phoneNumber: "199",
    dialNumber: "199",
    category: "FIRE_RESCUE",
    sourceUrl: "https://iao.bangkok.go.th/content-detail/27208",
    evidenceStatus: "LOCAL_REGIONAL_SERVICE",
    priority: 8,
    th: {
      serviceName: "สายด่วนดับเพลิงและกู้ภัย กรุงเทพมหานคร",
      purposeLabel: "แจ้งเหตุเพลิงไหม้และขอความช่วยเหลือด้านดับเพลิงในกรุงเทพมหานคร",
      jurisdiction: "กรุงเทพมหานครเท่านั้น",
      sourceAuthority: "กรุงเทพมหานคร",
      openingHoursNote: "24 ชั่วโมง",
      aliases: ["ไฟไหม้", "ดับเพลิง", "กู้ภัย", "กรุงเทพ"],
    },
    en: {
      serviceName: "Bangkok Fire and Rescue Hotline",
      purposeLabel: "Report fires and request Bangkok fire-service assistance",
      jurisdiction: "Bangkok only",
      sourceAuthority: "Bangkok Metropolitan Administration",
      openingHoursNote: "24 hours",
      aliases: ["fire", "fire service", "rescue", "Bangkok"],
    },
  },
  {
    key: "rama-poison-1367",
    phoneNumber: "1367",
    dialNumber: "1367",
    category: "POISON_CHEMICAL",
    sourceUrl:
      "https://www.rama.mahidol.ac.th/poisoncenter/sites/default/files/public/Protocol/New/Lead_poisoning_Ver2.pdf",
    evidenceStatus: "SCOPE_UNDER_REVIEW",
    priority: 9,
    th: {
      serviceName: "ศูนย์พิษวิทยารามาธิบดี",
      purposeLabel: "สายด่วนศูนย์พิษวิทยา",
      jurisdiction: "กลุ่มผู้ใช้ ขอบเขตบริการ และเวลาทำการอยู่ระหว่างตรวจสอบ",
      sourceAuthority: "คณะแพทยศาสตร์โรงพยาบาลรามาธิบดี มหาวิทยาลัยมหิดล",
      aliases: ["พิษ", "สารเคมี", "ยา", "ศูนย์พิษวิทยา"],
    },
    en: {
      serviceName: "Ramathibodi Poison Center",
      purposeLabel: "Poison Center hotline",
      jurisdiction: "Caller audience, service coverage and hours remain under review",
      sourceAuthority: "Faculty of Medicine Ramathibodi Hospital, Mahidol University",
      aliases: ["poison", "chemical", "medicine", "toxicology"],
    },
  },
  {
    key: "mea-1130",
    phoneNumber: "1130",
    dialNumber: "1130",
    category: "UTILITIES",
    sourceUrl: "https://www.mea.or.th/our-services/mea-service/e-service/JaLgVcdJV",
    evidenceStatus: "SCOPE_UNDER_REVIEW",
    priority: 10,
    th: {
      serviceName: "MEA Call Center",
      purposeLabel: "ติดต่อสอบถามบริการของการไฟฟ้านครหลวง",
      jurisdiction: "พื้นที่บริการการไฟฟ้านครหลวง — ขอบเขตพื้นที่อยู่ระหว่างตรวจสอบ",
      sourceAuthority: "การไฟฟ้านครหลวง",
      openingHoursNote: "24 ชั่วโมง",
      aliases: ["ไฟฟ้า", "กฟน", "MEA", "ไฟดับ"],
    },
    en: {
      serviceName: "MEA Call Center",
      purposeLabel: "Contact Metropolitan Electricity Authority customer services",
      jurisdiction: "MEA service area — exact geographic boundary remains under review",
      sourceAuthority: "Metropolitan Electricity Authority",
      openingHoursNote: "24 hours",
      aliases: ["electricity", "MEA", "power outage"],
    },
  },
  {
    key: "mwa-1125",
    phoneNumber: "1125",
    dialNumber: "1125",
    category: "UTILITIES",
    sourceUrl: "https://dast.mwa.co.th/home/",
    evidenceStatus: "SCOPE_UNDER_REVIEW",
    priority: 11,
    th: {
      serviceName: "MWA Call Center",
      purposeLabel: "ติดต่อสอบถามบริการน้ำประปาของการประปานครหลวง",
      jurisdiction: "พื้นที่บริการการประปานครหลวง — ขอบเขตพื้นที่อยู่ระหว่างตรวจสอบ",
      sourceAuthority: "การประปานครหลวง",
      openingHoursNote: "24 ชั่วโมง",
      aliases: ["น้ำประปา", "กปน", "MWA", "ท่อแตก"],
    },
    en: {
      serviceName: "MWA Call Center",
      purposeLabel: "Contact Metropolitan Waterworks Authority customer services",
      jurisdiction: "MWA service area — exact geographic boundary remains under review",
      sourceAuthority: "Metropolitan Waterworks Authority",
      openingHoursNote: "24 hours",
      aliases: ["water", "MWA", "water pipe"],
    },
  },
  {
    key: "pwa-1662",
    phoneNumber: "1662",
    dialNumber: "1662",
    category: "UTILITIES",
    sourceUrl: "https://www.pwa.co.th/contents/files/service/form/15/service-form-15-01.pdf",
    evidenceStatus: "SCOPE_UNDER_REVIEW",
    priority: 12,
    th: {
      serviceName: "PWA Contact Center",
      purposeLabel: "ติดต่อบริการและแจ้งปัญหาท่อประปาในพื้นที่สาขา กปภ.",
      jurisdiction: "พื้นที่สาขาการประปาส่วนภูมิภาค — ขอบเขตพื้นที่อยู่ระหว่างตรวจสอบ",
      sourceAuthority: "การประปาส่วนภูมิภาค",
      openingHoursNote: "24 ชั่วโมง",
      aliases: ["น้ำประปา", "กปภ", "PWA", "ท่อแตก", "ท่อรั่ว"],
    },
    en: {
      serviceName: "PWA Contact Center",
      purposeLabel: "Water-service contact and pipe-problem reports within PWA branches",
      jurisdiction: "PWA branch service areas — exact geographic boundary remains under review",
      sourceAuthority: "Provincial Waterworks Authority",
      openingHoursNote: "24 hours",
      aliases: ["water", "PWA", "broken pipe", "leak"],
    },
  },
  {
    key: "highway-police-1193",
    phoneNumber: "1193",
    dialNumber: "1193",
    category: "ROAD_TRAVEL",
    sourceUrl: "https://www.thailand.go.th/useful-information-detail/009_152?hl=th",
    evidenceStatus: "SCOPE_UNDER_REVIEW",
    priority: 13,
    th: {
      serviceName: "ตำรวจทางหลวง",
      purposeLabel: "ติดต่อบริการตำรวจทางหลวง",
      jurisdiction: "วัตถุประสงค์ปฏิบัติการและขอบเขตบริการอยู่ระหว่างตรวจสอบ",
      sourceAuthority: "พอร์ทัลรัฐบาลไทย",
      aliases: ["ตำรวจทางหลวง", "ทางหลวง", "ถนน"],
    },
    en: {
      serviceName: "Highway Police",
      purposeLabel: "Contact the Highway Police",
      jurisdiction: "Operational purpose and service coverage remain under review",
      sourceAuthority: "Thailand Government Portal",
      aliases: ["highway police", "highway", "road"],
    },
  },
  {
    key: "msdhs-1300",
    phoneNumber: "1300",
    dialNumber: "1300",
    category: "SOCIAL_SUPPORT",
    sourceUrl:
      "https://www.m-society.go.th/ewtadmin/ewt/mso_web/download/article/article_20211109112004.pdf",
    evidenceStatus: "SCOPE_UNDER_REVIEW",
    priority: 14,
    th: {
      serviceName: "ศูนย์ช่วยเหลือสังคม สายด่วน 1300",
      purposeLabel: "รับแจ้งความต้องการและประสานความช่วยเหลือทางสังคม",
      jurisdiction: "ประเทศไทย — ขอบเขตกลุ่มผู้รับบริการล่าสุดอยู่ระหว่างตรวจสอบ",
      sourceAuthority: "กระทรวงการพัฒนาสังคมและความมั่นคงของมนุษย์",
      openingHoursNote: "24 ชั่วโมง",
      aliases: ["สังคม", "ครอบครัว", "ช่วยเหลือ", "เด็ก", "ผู้สูงอายุ"],
    },
    en: {
      serviceName: "Social Assistance Centre Hotline",
      purposeLabel: "Receive social-assistance requests and coordinate referrals",
      jurisdiction: "Thailand — current recipient-group boundaries remain under review",
      sourceAuthority: "Ministry of Social Development and Human Security",
      openingHoursNote: "24 hours",
      aliases: ["social assistance", "family", "child", "older adult"],
    },
  },
  {
    key: "dld-0632256888",
    phoneNumber: "063-225-6888",
    dialNumber: "0632256888",
    category: "ANIMAL_PET",
    sourceUrl: "https://planning.dld.go.th/images/File/annualreport/Annual-2566.pdf",
    evidenceStatus: "SCOPE_UNDER_REVIEW",
    priority: 15,
    th: {
      serviceName: "สายด่วนกรมปศุสัตว์",
      purposeLabel: "แจ้งเบาะแสตามภารกิจกรมปศุสัตว์ ไม่ใช่สายด่วนกู้ภัยสัตว์เลี้ยงทั่วไป",
      jurisdiction: "ขอบเขตบริการอยู่ระหว่างตรวจสอบ",
      sourceAuthority: "กรมปศุสัตว์",
      openingHoursNote: "24 ชั่วโมง ตามเอกสารที่อ้างอิง",
      aliases: ["ปศุสัตว์", "แจ้งเบาะแส", "โรคสัตว์"],
    },
    en: {
      serviceName: "Department of Livestock Development Hotline",
      purposeLabel: "Reports within DLD functions; not a general pet-rescue hotline",
      jurisdiction: "Service coverage remains under review",
      sourceAuthority: "Department of Livestock Development",
      openingHoursNote: "24 hours according to the cited document",
      aliases: ["livestock", "animal disease", "report"],
    },
  },
  {
    key: "bma-1555",
    phoneNumber: "1555",
    dialNumber: "1555",
    category: "LOCAL_REGIONAL",
    sourceUrl: "https://webportal.bangkok.go.th/rongtook",
    evidenceStatus: "LOCAL_REGIONAL_SERVICE",
    priority: 16,
    th: {
      serviceName: "ศูนย์ กทม. 1555",
      purposeLabel: "แจ้งปัญหาและสอบถามบริการของกรุงเทพมหานคร",
      jurisdiction: "กรุงเทพมหานครเท่านั้น",
      sourceAuthority: "กรุงเทพมหานคร",
      aliases: ["กทม", "กรุงเทพ", "ร้องทุกข์", "บริการเมือง"],
    },
    en: {
      serviceName: "Bangkok Contact Centre",
      purposeLabel: "Report problems and ask about Bangkok municipal services",
      jurisdiction: "Bangkok only",
      sourceAuthority: "Bangkok Metropolitan Administration",
      aliases: ["Bangkok", "municipal service", "complaint", "city service"],
    },
  },
] as const;

export const contactDirectoryRecords: readonly ContactDirectoryRecord[] = sourceEntries.flatMap(
  (entry) =>
    (["th", "en"] as const).map((language) => {
      const localized = entry[language];
      return {
        id: `official-${entry.key}-${language}`,
        serviceName: localized.serviceName,
        phoneNumber: entry.phoneNumber,
        dialNumber: entry.dialNumber,
        category: entry.category,
        purposeLabel: localized.purposeLabel,
        jurisdiction: localized.jurisdiction,
        language,
        sourceAuthority: localized.sourceAuthority,
        sourceUrl: entry.sourceUrl,
        sourceRetrievedAt,
        lastReviewedAt,
        reviewStatus: entry.evidenceStatus === "SOURCE_BOUND_DRAFT" ? "DRAFT" : "REVIEW_REQUIRED",
        evidenceStatus: entry.evidenceStatus,
        ...(localized.openingHoursNote === undefined
          ? {}
          : { openingHoursNote: localized.openingHoursNote }),
        aliases: localized.aliases,
        priority: entry.priority,
        recordKind: "OFFICIAL_SOURCE_BACKED",
        editorialRecordId: `candidate-${entry.key}-${language}`,
      } satisfies ContactDirectoryRecord;
    }),
);

export function normalizeContactQuery(value: string): string {
  return value
    .normalize("NFKC")
    .toLocaleLowerCase()
    .replace(/[\s\p{P}\p{S}]+/gu, "");
}

export function filterContactRecords(
  records: readonly ContactDirectoryRecord[],
  locale: Locale,
  query: string,
  category: ContactCategory | "ALL",
): readonly ContactDirectoryRecord[] {
  const needle = normalizeContactQuery(query);
  return records
    .filter((record) => record.language === locale)
    .filter((record) => category === "ALL" || record.category === category)
    .filter(
      (record) =>
        needle.length === 0 ||
        [
          record.serviceName,
          record.phoneNumber,
          record.purposeLabel,
          record.jurisdiction,
          ...record.aliases,
        ].some((value) => normalizeContactQuery(value).includes(needle)),
    )
    .sort((a, b) => a.priority - b.priority || a.serviceName.localeCompare(b.serviceName, locale));
}
